import {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  redirect,
} from "react-router-dom";
import { message } from "antd";
import { getAuth } from "firebase/auth";
import { auth } from "~/lib/firebase";
import {
  apiClient,
  ServiceRequestDetailsLoaderResult,
} from "~/services/apiClient";
import { INTENTS } from "~/utils";
import { sendEmail } from "~/services/email/sendEmail";
import type { EmailObject } from "~/types/email";
import * as APITypes from "~/API";

export const ServiceRequestDetailsPageLoader = async ({
  params,
}: LoaderFunctionArgs) => {
  const user = auth.currentUser;
  if (!user) return redirect("/signup");

  const requestID = params.requestID || "";
  if (!requestID) return redirect("/service-request");

  const result = await apiClient.loader<ServiceRequestDetailsLoaderResult>(
    "service-request-details",
    { id: requestID }
  );

  if (!result.success || !result.data) {
    console.error("Service request details loader failed:", result.error);
    throw new Response(result.error ?? "Failed to load service request", {
      status: 500,
    });
  }

  return {
    user: {
      userID: user.uid,
      tenantID: (user as { tenantId?: string }).tenantId ?? "",
      tenantName: user.displayName ?? "",
    },
    serviceRequest: result.data.serviceRequest,
  };
};

export type ServiceRequestDetailsPageLoaderType = {
  user: {
    userID: string;
    tenantID: string;
    tenantName: string;
  };
  serviceRequest: APITypes.ServiceRequest;
};

export const ServiceRequestDetailsPageAction = async ({
  request,
}: ActionFunctionArgs) => {
  const formData = await request.formData();
  const authUser = getAuth().currentUser;
  if (!authUser) {
    throw new Response(JSON.stringify({ error: "Not authenticated" }), {
      status: 401,
    });
  }

  try {
    const payload = JSON.parse((formData.get("body") as string) ?? "{}") as {
      type: APITypes.ServiceRequestType;
      description: string;
      title: string;
      emergency: boolean;
      isUpgrade: boolean;
    };

    const result = await apiClient.sendIntent<{
      serviceRequest: unknown;
      emailObject: EmailObject;
    }>(INTENTS._add_service_request, {
      authUID: authUser.uid,
      creatorName: authUser.displayName ?? "",
      ...payload,
    });

    if (!result.success || !result.data) {
      throw new Error(result.error ?? "Failed to create service request");
    }

    const emailResult = await sendEmail(result.data.emailObject as EmailObject);
    if (!emailResult.success) {
      message.error("Service request created but email failed to send");
    } else {
      message.success("Service request added!");
    }
    return redirect("/");
  } catch (error) {
    const err = error as Error;
    message.error(err.message ?? "Failed to add service request");
    throw new Response(JSON.stringify({ error: err.message ?? String(error) }), {
      status: 500,
    });
  }
};
